import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  BriefcaseIcon,
  WrenchScrewdriverIcon,
  NoSymbolIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Card, CardBody } from '../ui/Card';
import { SectionCardHeader } from '../ui/SectionCard';
import PageHeader from '../ui/PageHeader';
import StatusPill from '../ui/StatusPill';
import { Alert } from '../ui/Alert';
import { useToast, TimePicker } from '../ui';
import api, { settingsAPI } from '../../services/api';

// ─── Type Editor Row ─────────────────────────────────────────────────────────

/** A row is inactive when either migrated (`active`) or legacy (`isActive`) flag is false. */
export const isInactive = (item) => item?.active === false || item?.isActive === false;

// Iter18 §2.15: two edit modes: name inline; description via a small prompt so operators
// don't need a separate modal. "Make default" clears the isDefault flag on sibling rows.
function TypeRow({ item, onUpdate, onDelete, onMakeDefault }) {
  const [editing, setEditing] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description || '');
  const [showMenu, setShowMenu] = useState(false);
  const menuBtnRef = React.useRef(null);

  const handleSave = () => {
    if (name.trim() && name !== item.name) {
      onUpdate(item.id, { name: name.trim() });
    }
    setEditing(false);
  };

  const handleSaveDesc = () => {
    if (description !== (item.description || '')) {
      onUpdate(item.id, { description });
    }
    setEditingDesc(false);
  };

  const handleCancel = () => {
    setName(item.name);
    setEditing(false);
  };

  const handleCancelDesc = () => {
    setDescription(item.description || '');
    setEditingDesc(false);
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 group">
      {editing ? (
        <div className="flex items-center gap-2 flex-1">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
            className="input flex-1 text-sm"
            autoFocus
          />
          <button onClick={handleSave} className="text-green-600 hover:text-green-700">
            <CheckIcon className="h-4 w-4" />
          </button>
          <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : editingDesc ? (
        <div className="flex items-center gap-2 w-full">
          <span className="text-sm text-gray-500 flex-shrink-0 truncate max-w-[8rem]">
            {item.name} —
          </span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleSaveDesc(); }
              if (e.key === 'Escape') { e.preventDefault(); handleCancelDesc(); }
            }}
            placeholder="Description (optional)"
            className="input flex-1 text-sm"
            autoFocus
          />
          <button onClick={handleSaveDesc} className="text-green-600 hover:text-green-700" aria-label="Save description">
            <CheckIcon className="h-4 w-4" />
          </button>
          <button onClick={handleCancelDesc} className="text-gray-400 hover:text-gray-600" aria-label="Cancel">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {item.color && (
              <div className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">{item.name}</span>
                {item.isDefault && (
                  <span className="text-[10px] bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded font-medium">
                    Default
                  </span>
                )}
                {/* IDI-407: migrated inactive types (e.g. 1st Choice's Commercial TI,
                    Education, Healthcare, Warehouse) looked current. */}
                {isInactive(item) && <StatusPill status="inactive" size="sm" />}
              </div>
              {item.description && (
                <p className="text-xs text-gray-500 truncate">{item.description}</p>
              )}
            </div>
          </div>
          <div className="relative">
            <button
              ref={menuBtnRef}
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div
                  className={`absolute right-0 z-20 w-40 card py-1 ${
                    menuBtnRef.current && menuBtnRef.current.getBoundingClientRect().bottom > window.innerHeight - 160
                      ? 'bottom-full mb-1'
                      : 'top-full mt-1'
                  }`}
                >
                  <button
                    onClick={() => { setEditing(true); setShowMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Rename
                  </button>
                  <button
                    onClick={() => { setEditingDesc(true); setShowMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Edit description
                  </button>
                  {!item.isDefault && (
                    <button
                      onClick={() => { onMakeDefault?.(item.id); setShowMenu(false); }}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Make default
                    </button>
                  )}
                  {!item.isDefault && (
                    <button
                      onClick={() => { onDelete(item.id); setShowMenu(false); }}
                      className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Type Section ────────────────────────────────────────────────────────────

// Iter18 R2 Round 3 (E) rev-5-superseded 2026-07-31 (§3.1 SectionHeader wave):
// Section headers now use <SectionCardHeader> (icon + title + description +
// right-floated inline Add) inside the section's own Card. Previously used
// per-section <PageHeader> (multiple <h1> on one page). Change matches the
// Customer Types reference and the mount pattern used by Competitors, Vendors,
// Notifications, and Support post-supersession.
// See SectionCard.js docstring for full supersession note.
function TypeSection({ title, description, icon, addLabel, items, onAdd, onUpdate, onDelete, onMakeDefault }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  const handleAdd = () => {
    if (newName.trim()) {
      onAdd(newName.trim());
      setNewName('');
      setAdding(false);
    }
  };

  return (
    <Card>
      <SectionCardHeader
        title={title}
        description={description}
        icon={icon}
        actions={
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            <PlusIcon className="h-4 w-4 mr-1" aria-hidden="true" /> {addLabel || 'Add'}
          </Button>
        }
      />
      <CardBody className="p-0">
        {items.length === 0 && !adding ? (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-gray-400">No types configured</p>
          </div>
        ) : (
          <div>
            {items.map(item => (
              <TypeRow
                key={item.id}
                item={item}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onMakeDefault={onMakeDefault}
              />
            ))}
          </div>
        )}
        {adding && (
          <div className="flex items-center gap-2 px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') { setAdding(false); setNewName(''); }
              }}
              placeholder="Type name..."
              className="input flex-1 text-sm"
              autoFocus
            />
            <button onClick={handleAdd} className="text-green-600 hover:text-green-700">
              <CheckIcon className="h-4 w-4" />
            </button>
            <button onClick={() => { setAdding(false); setNewName(''); }} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ─── Main Settings Page ──────────────────────────────────────────────────────

export default function JobWOTypesSettings() {
  const [jobTypes, setJobTypes] = useState([
    { id: '1', name: 'Residential', isDefault: true },
    { id: '2', name: 'Commercial', isDefault: false },
    { id: '3', name: 'HOA', isDefault: false },
  ]);
  const [woTypes, setWoTypes] = useState([
    { id: '1', name: 'Spray Foam', description: 'Spray foam insulation work', isDefault: true },
    { id: '2', name: 'Fiberglass', description: 'Fiberglass insulation work', isDefault: false },
    { id: '3', name: 'Blow', description: 'Blown-in insulation work', isDefault: false },
    { id: '4', name: 'Warranty Work', description: 'Warranty-covered service', isDefault: false },
    { id: '5', name: 'Pre-Work', description: 'Preparation before insulation', isDefault: false },
    { id: '6', name: 'Removal', description: 'Remove existing insulation', isDefault: false },
    { id: '7', name: 'Repair', description: 'Fix or repair existing work', isDefault: false },
    { id: '8', name: 'Installation', description: 'General installation work', isDefault: false },
  ]);
  const [cancellationReasons, setCancellationReasons] = useState([
    { id: '1', name: 'Customer cancelled', description: 'Customer asked to cancel', isDefault: false },
    { id: '2', name: 'Crew or resource unavailable', description: 'Crew or resource scheduling conflict', isDefault: false },
    { id: '3', name: 'Weather', description: 'Cancelled due to weather conditions', isDefault: false },
    { id: '4', name: 'Access blocked', description: 'Unable to access job site', isDefault: false },
    { id: '5', name: 'Other', description: 'Other reason (free text)', isDefault: true },
  ]);
  const [, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // R20-11b: scheduling defaults moved from Quote Defaults to this page.
  // Storage stays under `quote_defaults` (data-shape unchanged) — this page
  // is now the sole UI surface for editing WO start/end times.
  const [schedulingDefaults, setSchedulingDefaults] = useState({
    wo_default_start_time: '08:00',
    wo_default_end_time: '17:00',
  });
  const toast = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await api.getSettings();
      if (data?.jobTypes) setJobTypes(data.jobTypes);
      // IDI-387: the stored key + the picker's enum resolver both use
      // `workOrderTypes` (backend list_type=work_order_types -> workOrderTypes;
      // confirmed live on tenant 1: workOrderTypes[5], woTypes ABSENT). This
      // page previously read/wrote `woTypes`, a key nothing else touched, so
      // its edits never reached the WO-type pickers and it showed hardcoded
      // defaults instead of the tenant's real types. Read workOrderTypes first,
      // woTypes as a fallback for anything saved the old (broken) way.
      const woTypesList = data?.workOrderTypes ?? data?.woTypes;
      if (woTypesList) setWoTypes(woTypesList);
      if (data?.cancellationReasons) setCancellationReasons(data.cancellationReasons);
    } catch (err) {
      // Use defaults
    }
    // R20-11b: also load scheduling defaults from quote_defaults storage.
    try {
      const qd = await settingsAPI.getQuoteDefaults();
      setSchedulingDefaults({
        wo_default_start_time: qd?.wo_default_start_time || '08:00',
        wo_default_end_time: qd?.wo_default_end_time || '17:00',
      });
    } catch (err) {
      // Use defaults
    }
  };

  // R20-11b: save scheduling defaults back to quote_defaults storage.
  // Merges with any other fields already there (so this doesn't clobber the
  // fields still edited on the Quote Defaults page).
  const saveScheduling = async (patch) => {
    const next = { ...schedulingDefaults, ...patch };
    setSchedulingDefaults(next);
    try {
      // Read existing quote_defaults to preserve non-scheduling fields
      const existing = (await settingsAPI.getQuoteDefaults()) || {};
      await settingsAPI.updateQuoteDefaults({ ...existing, ...next });
      toast.success('Scheduling defaults saved');
    } catch (err) {
      setError('Failed to save scheduling defaults');
    }
  };

  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      await api.updateSettings(updates);
      toast.success('Settings saved');
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Job Types handlers
  const addJobType = (name) => {
    const newType = { id: `jt-${Date.now()}`, name, isDefault: false };
    const updated = [...jobTypes, newType];
    setJobTypes(updated);
    saveSettings({ jobTypes: updated });
  };

  const updateJobType = (id, updates) => {
    const updated = jobTypes.map(t => t.id === id ? { ...t, ...updates } : t);
    setJobTypes(updated);
    saveSettings({ jobTypes: updated });
  };

  const deleteJobType = (id) => {
    const updated = jobTypes.filter(t => t.id !== id);
    setJobTypes(updated);
    saveSettings({ jobTypes: updated });
  };

  // Iter18 §2.15: "Make default" flips exactly one row's isDefault flag to true
  // and clears it on all siblings. Same shape for WO Types and Cancellation Reasons.
  const makeJobTypeDefault = (id) => {
    const updated = jobTypes.map(t => ({ ...t, isDefault: t.id === id }));
    setJobTypes(updated);
    saveSettings({ jobTypes: updated });
  };

  // WO Types handlers
  const addWoType = (name) => {
    const newType = { id: `wt-${Date.now()}`, name, isDefault: false };
    const updated = [...woTypes, newType];
    setWoTypes(updated);
    saveSettings({ workOrderTypes: updated });
  };

  const updateWoType = (id, updates) => {
    const updated = woTypes.map(t => t.id === id ? { ...t, ...updates } : t);
    setWoTypes(updated);
    saveSettings({ workOrderTypes: updated });
  };

  const deleteWoType = (id) => {
    const updated = woTypes.filter(t => t.id !== id);
    setWoTypes(updated);
    saveSettings({ workOrderTypes: updated });
  };

  const makeWoTypeDefault = (id) => {
    const updated = woTypes.map(t => ({ ...t, isDefault: t.id === id }));
    setWoTypes(updated);
    saveSettings({ workOrderTypes: updated });
  };

  // Cancellation Reasons handlers
  const addCancellationReason = (name) => {
    const newReason = { id: `cr-${Date.now()}`, name, isDefault: false };
    const updated = [...cancellationReasons, newReason];
    setCancellationReasons(updated);
    saveSettings({ cancellationReasons: updated });
  };

  const updateCancellationReason = (id, updates) => {
    const updated = cancellationReasons.map(r => r.id === id ? { ...r, ...updates } : r);
    setCancellationReasons(updated);
    saveSettings({ cancellationReasons: updated });
  };

  const deleteCancellationReason = (id) => {
    const updated = cancellationReasons.filter(r => r.id !== id);
    setCancellationReasons(updated);
    saveSettings({ cancellationReasons: updated });
  };

  const makeCancellationReasonDefault = (id) => {
    const updated = cancellationReasons.map(r => ({ ...r, isDefault: r.id === id }));
    setCancellationReasons(updated);
    saveSettings({ cancellationReasons: updated });
  };

  return (
    <div>
      {/* Rev-5-superseded 2026-07-31 (§3.1 SectionHeader wave): restored page-
          level <PageHeader> to match the Customer Types reference; each section
          renders <SectionCardHeader> inside its own Card. See SectionCard.js
          docstring for supersession note. */}
      <PageHeader
        title="Jobs & Work Orders"
        description="Categories for jobs, types of work orders within a job, and reasons for cancellation."
      />

      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

      <div className="space-y-4">
        <TypeSection
          title="Job Types"
          description="Categories for organizing jobs (e.g., Residential, Commercial)"
          icon={BriefcaseIcon}
          addLabel="Add type"
          items={jobTypes}
          onAdd={addJobType}
          onUpdate={updateJobType}
          onDelete={deleteJobType}
          onMakeDefault={makeJobTypeDefault}
        />

        <TypeSection
          title="Work Order Types"
          description="Types of work orders that can be created within a job"
          icon={WrenchScrewdriverIcon}
          addLabel="Add type"
          items={woTypes}
          onAdd={addWoType}
          onUpdate={updateWoType}
          onDelete={deleteWoType}
          onMakeDefault={makeWoTypeDefault}
        />

        <TypeSection
          title="Cancellation Reasons"
          description="Reasons available when cancelling a work order"
          icon={NoSymbolIcon}
          addLabel="Add reason"
          items={cancellationReasons}
          onAdd={addCancellationReason}
          onUpdate={updateCancellationReason}
          onDelete={deleteCancellationReason}
          onMakeDefault={makeCancellationReasonDefault}
        />

        {/* R20-11b: Scheduling Defaults moved here from Quote Defaults.
            Storage stays under `quote_defaults` (data-shape unchanged);
            this is now the sole UI surface for editing WO start/end times. */}
        <Card>
          <SectionCardHeader
            title="Scheduling Defaults"
            description="Default start and end times for new work orders"
            icon={ClockIcon}
          />
          <CardBody>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="wo-start" className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
                  WO Start Time
                </label>
                <TimePicker
                  id="wo-start"
                  value={schedulingDefaults.wo_default_start_time}
                  onChange={(val) => saveScheduling({ wo_default_start_time: val })}
                />
              </div>
              <div>
                <label htmlFor="wo-end" className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
                  WO End Time
                </label>
                <TimePicker
                  id="wo-end"
                  value={schedulingDefaults.wo_default_end_time}
                  onChange={(val) => saveScheduling({ wo_default_end_time: val })}
                />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
